import { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Eye, Heart, Package, Plus, Minus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ResponsiveImage } from '@/components/ui/responsive-image';
import type { ProductDisplay, ProductVariant } from '../types';
import { isApprovedProductAsset } from '@/lib/approved-product-images';
import { normalizeProductImages } from '@/lib/image-utils';
import { useShowPrices, useUnlimitedStock, useSettings } from '@/features/settings/hooks/use-settings';
import { useProductInCart } from '@/features/cart/hooks/use-cart';

interface ProductCardProps {
  product: ProductDisplay;
  onAddToCart?: (product: ProductDisplay, variant?: ProductVariant) => void;
  onQuickView?: (product: ProductDisplay) => void;
  layout?: 'grid' | 'list';
  showActions?: boolean;
}

const UNLIMITED_STOCK_DISPLAY_THRESHOLD = 999999;

function getPreferredVariant(
  variants: ProductVariant[],
  unlimitedStock: boolean
): ProductVariant | undefined {
  return (
    variants.find((variant) => variant.isDefault && (unlimitedStock || variant.stock > 0)) ||
    variants.find((variant) => unlimitedStock || variant.stock > 0) ||
    variants.find((variant) => variant.isDefault) ||
    variants[0]
  );
}

function formatVariantLabel(volume: string) {
  return volume === 'REGULAR' ? 'Regular' : volume;
}

export function ProductCard({
  product,
  onAddToCart,
  onQuickView,
  layout = 'grid',
  showActions = true,
}: ProductCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [isVariantDialogOpen, setIsVariantDialogOpen] = useState(false);
  const variants = product.variants || [];
  const showPrices = useShowPrices();
  const unlimitedStock = useUnlimitedStock();
  const preferredVariant = useMemo(
    () => getPreferredVariant(variants, unlimitedStock),
    [unlimitedStock, variants]
  );
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(preferredVariant);
  const { settings } = useSettings();
  const waNumber = settings.whatsapp_business_number;

  useEffect(() => {
    setSelectedVariant((currentVariant) => {
      if (currentVariant && variants.some((variant) => variant.id === currentVariant.id)) {
        return currentVariant;
      }

      return preferredVariant;
    });
  }, [preferredVariant, variants]);

  const { isInCart: isProductInCart, quantity: cartQuantity } = useProductInCart(
    product.id,
    selectedVariant?.id
  );

  const displayPrice = selectedVariant?.price ?? product.price;
  const comparePrice = selectedVariant?.compareAtPrice ?? product.compareAtPrice;
  const stockQuantity = selectedVariant?.stock ?? product.stockQuantity;
  const hasVariants = variants.length > 0;
  const hasVariantChoices = variants.length > 1;
  const shouldHideStockCount =
    unlimitedStock || stockQuantity >= UNLIMITED_STOCK_DISPLAY_THRESHOLD;
  // Unlimited stock mode bypasses all stock checks
  const hasAnyStock = unlimitedStock ? true : (hasVariants ? variants.some((variant) => variant.stock > 0) : stockQuantity > 0);
  const isSelectedVariantOutOfStock = unlimitedStock ? false : (selectedVariant ? selectedVariant.stock <= 0 : !hasAnyStock);
  const isOutOfStock = unlimitedStock ? false : !hasAnyStock;
  const isLowStock = unlimitedStock ? false : (!isOutOfStock && stockQuantity > 0 && stockQuantity <= 5);
  const pricedVariantPrices = variants
    .map((variant) => variant.price)
    .filter((price): price is number => Number.isFinite(price) && price > 0);
  const displayPriceForUi = Number.isFinite(displayPrice) && displayPrice > 0
    ? displayPrice
    : pricedVariantPrices.length > 0
      ? Math.min(...pricedVariantPrices)
      : 0;
  const isPriceMissing = !(Number.isFinite(displayPriceForUi) && displayPriceForUi > 0);
  const showAskForPrice = !showPrices || isPriceMissing;
  const askForPriceUrl = waNumber
    ? `https://wa.me/${waNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I'd like to know the price of ${product.name}`)}`
    : undefined;
  const variantSummary = variants.map((variant) => formatVariantLabel(variant.volume)).join(' / ');

  const normalizedImages = normalizeProductImages(
    'image' in product ? (product as unknown as { image?: string }).image : undefined,
    product.images
  );
  const primaryImage = normalizedImages[0];
  const usesApprovedProductAsset = isApprovedProductAsset(primaryImage);
  const gridImageContainerClass = usesApprovedProductAsset ? 'bg-white p-1.5 sm:p-2' : 'bg-white p-3 sm:p-4';
  const gridImageClassName = usesApprovedProductAsset
    ? 'rounded-xl bg-white object-contain scale-[1.14] group-hover:scale-[1.18]'
    : 'rounded-xl bg-white object-contain';
  const listImageContainerClass = usesApprovedProductAsset ? 'bg-white p-1.5' : 'bg-white p-3';
  const listImageClassName = usesApprovedProductAsset
    ? 'rounded-lg bg-white object-contain scale-[1.12] group-hover:scale-[1.16]'
    : 'rounded-lg bg-white object-contain';
  const orderingDisabled = isSelectedVariantOutOfStock || isPriceMissing;

  useEffect(() => {
    if (!product) return;

    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || '',
      image: normalizedImages[0] ? [normalizedImages[0]] : [],
      sku: product.id,
      brand: {
        '@type': 'Brand',
        name: 'Karebe Wines & Spirits',
      },
      offers: {
        '@type': 'Offer',
        price: displayPrice,
        priceCurrency: 'KES',
        availability: isOutOfStock
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'Karebe Wines & Spirits',
        },
      },
    };

    let schemaScript = document.getElementById(
      `product-schema-${product.id}`
    ) as HTMLScriptElement | null;

    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = `product-schema-${product.id}`;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }

    schemaScript.textContent = JSON.stringify(productSchema);

    return () => {
      if (schemaScript && document.head.contains(schemaScript)) {
        document.head.removeChild(schemaScript);
      }
    };
  }, [displayPrice, isOutOfStock, normalizedImages, product]);

  const openProductConfigurator = () => {
    navigate(`/products/${product.id}`, {
      state: {
        focusCartConfigurator: true,
        selectedVariantId: selectedVariant?.id,
      },
    });
  };

  const handleQuickAction = () => {
    openProductConfigurator();
  };

  const handleVariantConfirm = () => {
    if (!isSelectedVariantOutOfStock && onAddToCart) {
      onAddToCart(product, selectedVariant);
    }
    setIsVariantDialogOpen(false);
  };

  const handleQuickView = () => {
    if (onQuickView) {
      onQuickView(product);
    }
  };

  const actionLabel = hasVariantChoices
    ? 'Choose size and quantity'
    : isProductInCart
      ? 'Add more or update quantity'
      : 'Choose quantity';

  const variantDialog = hasVariantChoices ? (
    <Dialog
      open={isVariantDialogOpen}
      onClose={() => setIsVariantDialogOpen(false)}
      title={product.name}
      description="Choose the quantity you want before adding this item to cart."
      size="md"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {variants.map((variant) => {
                  const selected = selectedVariant?.id === variant.id;
                  const variantOutOfStock = unlimitedStock ? false : variant.stock <= 0;
                  const shouldHideVariantStockCount =
                    unlimitedStock || variant.stock >= UNLIMITED_STOCK_DISPLAY_THRESHOLD;

            return (
              <button
                key={variant.id}
                type="button"
                onClick={() => setSelectedVariant(variant)}
                disabled={variantOutOfStock}
                className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                  selected
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-brand-200 bg-white text-brand-700 hover:border-brand-400'
                } ${variantOutOfStock ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{formatVariantLabel(variant.volume)}</span>
                  <span className="text-xs uppercase tracking-wide">
                    {variantOutOfStock ? 'Out' : shouldHideVariantStockCount ? 'Available' : `${variant.stock} left`}
                  </span>
                </div>
                {showPrices && (
                  <div className={`mt-2 text-sm ${selected ? 'text-white/90' : 'text-brand-500'}`}>
                    {variant.price > 0 ? `KES ${variant.price.toLocaleString()}` : 'Contact for price'}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {selectedVariant && (
          <div className="rounded-2xl bg-brand-50 px-4 py-3 text-sm text-brand-700">
            Selected: {formatVariantLabel(selectedVariant.volume)}
            {showPrices && ` • KES ${selectedVariant.price.toLocaleString()}`}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsVariantDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleVariantConfirm} disabled={isSelectedVariantOutOfStock}>
            {isProductInCart ? 'Remove selected option' : 'Add selected option'}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  ) : null;

  if (layout === 'grid') {
    return (
      <>
        <Card
          elevation={isHovered ? 'medium' : 'low'}
          className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-brand-100 bg-white transition-all duration-300"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative overflow-hidden border-b border-brand-100 bg-brand-50">
            <Link to={`/products/${product.id}`} className="block h-full w-full">
              <ResponsiveImage
                src={primaryImage}
                fallbackImages={normalizedImages.slice(1)}
                alt={product.name}
                productName={product.name}
                category={product.categoryName}
                aspectRatio="1/1"
                objectFit="contain"
                priority="low"
                hoverZoom
                className={gridImageContainerClass}
                imageClassName={gridImageClassName}
              />
            </Link>

            <div className="absolute left-3 top-3 flex flex-col gap-2">
              {product.isNewArrival && <Badge variant="info" size="sm">New</Badge>}
              {product.isPopular && <Badge variant="info" size="sm">Popular</Badge>}
              {product.isOnSale && comparePrice && <Badge variant="danger" size="sm">Offer</Badge>}
              {isOutOfStock && <Badge variant="secondary" size="sm">Out of Stock</Badge>}
              {isLowStock && <Badge variant="warning" size="sm">Low Stock</Badge>}
              {isProductInCart && (
                <Badge variant="success" size="sm" className="flex items-center gap-1">
                  <ShoppingCart className="h-3 w-3" />
                  {cartQuantity > 1 ? `In Cart (${cartQuantity})` : 'In Cart'}
                </Badge>
              )}
            </div>

            {showActions && (
              <div
                className={`absolute right-3 top-3 flex flex-col gap-2 transition-all duration-300 ${
                  isHovered ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                }`}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-white/90 shadow-sm backdrop-blur-sm hover:bg-white"
                  onClick={handleQuickView}
                  aria-label="Quick view"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-white/90 shadow-sm backdrop-blur-sm hover:bg-white"
                  aria-label="Add to wishlist"
                >
                  <Heart className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <CardContent className="flex flex-1 flex-col p-4 sm:p-5">
            {product.categoryName && (
              <p className="mb-1 text-xs text-brand-500">{product.categoryName}</p>
            )}

            <Link
              to={`/products/${product.id}`}
              className="mb-2 inline-block min-h-[3.25rem] line-clamp-2 font-medium text-brand-900 transition-colors group-hover:text-brand-600 hover:text-brand-600"
            >
              {product.name}
            </Link>

            {product.description && (
              <p className="mb-3 line-clamp-2 text-sm text-brand-600">{product.description}</p>
            )}

            {hasVariantChoices && (
              <button
                type="button"
                onClick={openProductConfigurator}
                className="mb-3 rounded-2xl border border-brand-200 bg-brand-50 px-3 py-2 text-left text-sm text-brand-700 transition-colors hover:border-brand-400"
              >
                <span className="block font-medium">Choose size and quantity</span>
                <span className="mt-1 block text-xs text-brand-500">{variantSummary}</span>
              </button>
            )}

            <div className="mt-auto flex items-end justify-between gap-3 pt-3">
              {showAskForPrice ? (
                askForPriceUrl ? (
                  <a
                    href={askForPriceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-green-200 transition-colors hover:bg-green-100"
                  >
                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.556 4.122 1.528 5.855L0 24l6.293-1.504A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.371l-.36-.213-3.727.891.924-3.638-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>
                    Ask for price
                  </a>
                ) : (
                  <span className="text-sm italic text-brand-500">Contact for pricing</span>
                )
              ) : (
                <div className="min-w-0">
                  <span className="block text-lg font-semibold text-brand-800">
                    KES {displayPriceForUi.toLocaleString()}
                  </span>
                  {comparePrice && displayPriceForUi > 0 && comparePrice > displayPriceForUi && (
                    <span className="text-sm text-brand-400 line-through">
                      KES {comparePrice.toLocaleString()}
                    </span>
                  )}
                </div>
              )}

              {showActions && (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleQuickAction}
                    disabled={orderingDisabled}
                    className={`relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full shadow-sm transition-all duration-200 hover:shadow-md ${
                      isProductInCart
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                        : 'bg-brand-600 text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50'
                    }`}
                    aria-label={actionLabel}
                    title={actionLabel}
                  >
                    {isProductInCart ? (
                      <>
                        <ShoppingCart className="h-5 w-5" />
                        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm">
                          <Plus className="h-3 w-3" />
                        </span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5" />
                        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                          {hasVariantChoices ? (
                            <span className="text-[10px] font-bold">...</span>
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {variantDialog}
      </>
    );
  }

  return (
    <>
      <Card
        elevation={isHovered ? 'medium' : 'low'}
        className="group flex flex-col gap-4 rounded-2xl border border-brand-100 bg-white p-4 transition-all duration-300 sm:flex-row"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link
          to={`/products/${product.id}`}
          className="relative block w-full flex-shrink-0 overflow-hidden rounded-xl border border-brand-100 bg-brand-50 sm:h-32 sm:w-32"
        >
          <ResponsiveImage
            src={primaryImage}
            fallbackImages={normalizedImages.slice(1)}
            alt={product.name}
            productName={product.name}
            category={product.categoryName}
            aspectRatio="1/1"
            objectFit="contain"
            priority="low"
            hoverZoom
            className={listImageContainerClass}
            imageClassName={listImageClassName}
          />
        </Link>

        <div className="min-w-0 flex-1">
          {product.categoryName && (
            <p className="mb-1 text-xs text-brand-500">{product.categoryName}</p>
          )}

          <Link
            to={`/products/${product.id}`}
            className="mb-2 block font-medium text-brand-900 transition-colors group-hover:text-brand-600 hover:text-brand-600"
          >
            {product.name}
          </Link>

          <p className="mb-3 line-clamp-2 text-sm text-brand-600">{product.description}</p>

            {hasVariantChoices && (
              <button
                type="button"
                onClick={openProductConfigurator}
                className="mb-3 rounded-2xl border border-brand-200 bg-brand-50 px-3 py-2 text-left text-sm text-brand-700 transition-colors hover:border-brand-400"
              >
                <span className="block font-medium">Choose size and quantity</span>
                <span className="mt-1 block text-xs text-brand-500">{variantSummary}</span>
              </button>
            )}

          <div className="flex items-center gap-4 text-sm text-brand-500">
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4" />
              {isOutOfStock ? 'Out of stock' : shouldHideStockCount ? 'Available' : `${stockQuantity} in stock`}
            </span>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-3 border-t border-brand-100 pt-3 sm:min-w-[160px] sm:items-end sm:justify-between sm:border-t-0 sm:pt-0">
          <div className="text-left sm:text-right">
            {showAskForPrice ? (
              askForPriceUrl ? (
                <a
                  href={askForPriceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-green-200 transition-colors hover:bg-green-100"
                >
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.556 4.122 1.528 5.855L0 24l6.293-1.504A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.371l-.36-.213-3.727.891.924-3.638-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>
                  Ask for price
                </a>
              ) : (
                <span className="text-sm italic text-brand-500">Contact for pricing</span>
              )
            ) : (
              <>
                <span className="block text-xl font-semibold text-brand-800">
                  KES {displayPriceForUi.toLocaleString()}
                </span>
                {comparePrice && displayPriceForUi > 0 && comparePrice > displayPriceForUi && (
                  <span className="text-sm text-brand-400 line-through">
                    KES {comparePrice.toLocaleString()}
                  </span>
                )}
              </>
            )}
          </div>

          {showActions && (
            <div className="flex w-full gap-2 sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleQuickView}
                className="flex-1 sm:flex-none"
              >
                <Eye className="mr-1 h-4 w-4" />
                View
              </Button>
              <Button
                size="sm"
                variant={isProductInCart ? 'primary' : 'outline'}
                onClick={handleQuickAction}
                disabled={orderingDisabled}
                className={`h-9 flex-1 p-0 sm:h-auto sm:w-9 sm:flex-none sm:aspect-square ${
                  isProductInCart ? 'bg-emerald-500 text-white hover:bg-emerald-600 border-emerald-500' : ''
                }`}
                title={actionLabel}
                aria-label={actionLabel}
              >
                {isProductInCart ? (
                  <Plus className="h-4 w-4" />
                ) : (
                  <ShoppingCart className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>
      </Card>
      {variantDialog}
    </>
  );
}

export function ProductCardSkeleton({ layout = 'grid' }: { layout?: 'grid' | 'list' }) {
  if (layout === 'grid') {
    return (
      <Card elevation="low" className="flex h-full flex-col overflow-hidden">
        <Skeleton className="aspect-square" />
        <CardContent className="space-y-3 p-4">
          <SkeletonText className="h-3 w-1/3" />
          <SkeletonText className="h-4 w-full" />
          <SkeletonText className="h-4 w-2/3" />
          <div className="flex items-center justify-between pt-2">
            <SkeletonText className="h-5 w-1/3" />
            <Skeleton className="h-10 w-10 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation="low" className="flex flex-col gap-4 p-4 sm:flex-row">
      <Skeleton className="aspect-square w-full flex-shrink-0 rounded-xl sm:h-32 sm:w-32" />
      <div className="flex-1 space-y-3">
        <SkeletonText className="h-3 w-1/4" />
        <SkeletonText className="h-5 w-3/4" />
        <SkeletonText className="h-4 w-full" />
        <SkeletonText className="h-4 w-1/2" />
      </div>
      <div className="w-full space-y-3 sm:w-32">
        <SkeletonText className="h-6 w-full" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-16 rounded-lg" />
          <Skeleton className="h-9 w-16 rounded-lg" />
        </div>
      </div>
    </Card>
  );
}

function SkeletonText({ className }: { className?: string }) {
  return <Skeleton className={`rounded ${className}`} />;
}
